import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
} from "react";
import useQueryString from "use-query-string";
import { Checkbox, Heading, Stack } from "@chakra-ui/react";

export type TutorialFilterConfig = {
  certifications: Set<string>;
  categories: Set<string>;
  types: Set<string>;
  paths: Set<string>;
  levels: Set<string>;
  languages: Set<string>;
};

export const FilterContext = createContext<{
  filter: ReturnType<typeof useQueryString>[0];
  setFilter: Dispatch<SetStateAction<TutorialFilterConfig>>;
} | null>(null);

const useFilter = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("Filter context used without provider");
  }
  return context;
};

type TutorialsFilterProps = {
  title: string;
  options: { value: string; label: string }[];
  filterKey: keyof TutorialFilterConfig;
};

export function TutorialsFilter({
  title,
  options,
  filterKey,
}: TutorialsFilterProps) {
  const { filter, setFilter } = useFilter();
  const filterByKey = filter[filterKey];
  const filterValues = filterByKey
    ? Array.isArray(filterByKey)
      ? filterByKey
      : [filterByKey]
    : [];
  return (
    <Stack spacing="3" align="flex-start">
      <Heading as="h4" size="md">
        {title}
      </Heading>
      {options.map((option, index) => (
        <Checkbox
          colorScheme="indigo"
          key={index}
          isChecked={filterValues.includes(option.value)}
          onChange={(event) =>
            setFilter((prev) => ({
              ...prev,
              [filterKey]: event.target.checked
                ? [...filterValues, option.value]
                : filterValues.filter((value) => value !== option.value),
            }))
          }
        >
          {option.label}
        </Checkbox>
      ))}
    </Stack>
  );
}
